import { filter } from 'rxjs/operators';

import type {
  ExtractDispatchFromModel,
  ExtractSelectFromModel,
  WidgetEffectParams
} from '@swf/core/store';
import { UserClubRole } from '@swf/core/types/Poker';
import { canUseDOM } from '@swf/core/utils';
import type {
  GetFilteredClubInfoRequest,
  GetFilteredClubInfoResponse,
  GetPlayersBasedOnStatusRequest,
  GetPlayersBasedOnStatusResponse,
  GetUserClubsResponse,
  HgClubInfo,
  HgGetPlayersBasedOnStatus
} from '@swf/generated/types/api/v0/SwHomeGames';
import type { HomeGamesWidgetModel } from '@swf/poker';
import { PKR_AVATAR_COUNT, PKR_LOADERS } from '@swf/poker';

import { clubOptions } from './effects/clubOptions';
import { fetchClubSeasonStats } from './effects/fetchClubSeasonStats';
import { fetchClubSeasons } from './effects/fetchClubSeasons';
import { fetchInitialData } from './effects/fetchInitialData';
import { fetchLobbyData } from './effects/fetchLobbyData';
import { fetchPlayerTournamentResults } from './effects/fetchPlayerTournamentResults';
import { fetchRecentTournamentSummaries } from './effects/fetchRecentTournamentSummaries';
import { fetchRegisteredTournaments } from './effects/fetchRegisteredTournaments';

const effects = {
  async preload(_: undefined, params: WidgetEffectParams<void>): Promise<void> {
    const {
      actions,
      select,
      rootState,
      dispatch,
      widget: { id }
    } = params;
    const isClientAuthenticated = select.session.isClientAuthenticated(rootState);
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[id];

    if (isClientAuthenticated) {
      fetchInitialData(undefined, params);
    }

    if (canUseDOM) {
      actions
        .pipe(
          filter(
            ({ type, state }) =>
              type === 'session/response' && select.session.isClientAuthenticated(state)
          )
        )
        .subscribe(async () => {
          widgetDispatch.setLoading({ isLoading: true, loaderName: PKR_LOADERS.initial });
          fetchInitialData(undefined, params);
        });

      actions
        .pipe(
          filter(
            ({ type, state }) =>
              type === 'session/response' && !select.session.isClientAuthenticated(state)
          )
        )
        .subscribe(async () => {
          widgetDispatch.setLoading({ isLoading: false, loaderName: PKR_LOADERS.initial });
        });
    }
  },

  async fetchUserClubs(
    _: undefined,
    { request, dispatch, widget: { id }, select, rootState }: WidgetEffectParams<void>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[id];
    const loaderName = PKR_LOADERS.userClubs;
    const isClientAuthenticated = select.session.isClientAuthenticated(rootState);

    if (!isClientAuthenticated) return;

    widgetDispatch.setLoading({ isLoading: true, loaderName });

    const { body } = await request<GetUserClubsResponse>({
      service: 'homeGames.userClubsRequest'
    });

    widgetDispatch.setUserClubs(body);
    widgetDispatch.setLoading({ isLoading: false, loaderName });
  },

  async fetchClubInfo(
    _: undefined,
    { request, dispatch, widget: { id }, select, rootState }: WidgetEffectParams<void>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[id];
    const widgetSelect: ExtractSelectFromModel<HomeGamesWidgetModel> = select[id];
    const loaderName = PKR_LOADERS.clubInfo;
    const isClientAuthenticated = select.session.isClientAuthenticated(rootState);

    if (!isClientAuthenticated) return;

    const { clubs } = widgetSelect.get(rootState);

    widgetDispatch.setLoading({ isLoading: true, loaderName });

    const {
      body: { filteredClubInfoReply }
    } = await request<GetFilteredClubInfoResponse>({
      service: 'homeGames.filteredClubInfoRequest',
      body: {
        gameFilter: {
          gameCategoryMask: 255,
          clubIds: clubs
            .filter(({ roleForUser }) => roleForUser !== UserClubRole.Suspended)
            .map(({ clubId }: HgClubInfo) => clubId)
        } as GetFilteredClubInfoRequest
      }
    });

    widgetDispatch.setUserClubsAndGames({ clubInfoList: clubs, filteredClubInfoReply });
    widgetDispatch.setLoading({ isLoading: false, loaderName });
  },

  async fetchClubAvatars(
    {
      clubId,
      avatarIncluded = true,
      limit = PKR_AVATAR_COUNT,
      ignoreCache = false
    }: Omit<HgGetPlayersBasedOnStatus, 'clubId'> & {
      clubId: number | string;
      ignoreCache?: boolean;
    },
    { request, dispatch, widget: { id }, select, rootState }: WidgetEffectParams<void>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[id];
    const loaderName = PKR_LOADERS.playersByClubId(clubId);
    const widgetSelect: ExtractSelectFromModel<HomeGamesWidgetModel> = select[id];
    const { playersByClubId } = widgetSelect.get(rootState);

    if (!ignoreCache && playersByClubId[clubId]) return;

    widgetDispatch.setLoading({ isLoading: true, loaderName });

    const { body } = await request<GetPlayersBasedOnStatusResponse>({
      service: 'homeGames.getPlayersBasedOnStatusRequest',
      body: {
        getPlayersBasedOnStatus: {
          avatarIncluded,
          clubId,
          limit,
          statuses: ['HG_MEMBERSHIP_STATUS_APPROVED']
        }
      } as GetPlayersBasedOnStatusRequest
    });

    if (body && body.getPlayersBasedOnStatusReply && body.getPlayersBasedOnStatusReply.playerList) {
      widgetDispatch.setPlayersByClubId({
        clubId,
        response: body?.getPlayersBasedOnStatusReply?.playerList
      });
    }

    widgetDispatch.setLoading({ isLoading: false, loaderName });
  },

  async fetchClubMembers(
    {
      clubId,
      statuses
    }: Omit<HgGetPlayersBasedOnStatus, 'clubId'> & {
      clubId: number | string;
    },
    { request, dispatch, widget: { id } }: WidgetEffectParams<void>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<HomeGamesWidgetModel> = dispatch[id];
    const loaderName = PKR_LOADERS.clubMembers;
    widgetDispatch.setLoading({ isLoading: true, loaderName });

    const { body } = await request<GetPlayersBasedOnStatusResponse>({
      service: 'homeGames.getPlayersBasedOnStatusRequest',
      body: {
        getPlayersBasedOnStatus: {
          clubId,
          statuses
        }
      } as GetPlayersBasedOnStatusRequest
    });

    if (body && body.getPlayersBasedOnStatusReply && body.getPlayersBasedOnStatusReply.playerList) {
      widgetDispatch.setClubMembers({
        response: body?.getPlayersBasedOnStatusReply?.playerList
      });
    }

    widgetDispatch.setLoading({ isLoading: false, loaderName });
  },

  fetchRegisteredTournaments,
  fetchClubSeasons,
  fetchClubSeasonStats,
  clubOptions,
  fetchLobbyData,
  fetchRecentTournamentSummaries,
  fetchPlayerTournamentResults,
  fetchInitialData
};

export default effects;
